import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form, Input, DatePicker, Radio, Space, Button, Modal, Divider, message } from 'antd';
import FormValidationHelper from 'utils/validation';
import LicenseImg from 'components/LicenseImg';
import StateSelect from 'components/form/StateSelect';
import s from './s.module.less';
import dayjs from 'dayjs';
import { ILicenseData, LicenseData } from 'types/newUniprofile';
import { providerLicenseBatchByType } from 'api/provider';
import { ELicenseType } from 'types/common';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import { isEqual } from 'lodash';

interface FormDeaLicense {
    needLicense: boolean;
    licenses: LicenseData[];
}

interface Props {
    initValue?: ILicenseData[];
    isEditing?: boolean;
    refetch?: () => void;
    scheduleToken?: string;
}

const DeaLicense = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, refetch, scheduleToken } = props;
    const [form] = Form.useForm<FormDeaLicense>();
    const [showRemoveWarning, setShowRemoveWarning] = React.useState(false);
    const needLicenseValue = Form.useWatch('needLicense', form);
    const prevInitValueRef = useRef<ILicenseData[]>();

    useEffect(() => {
        if (initValue?.length && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            form.setFieldsValue({
                needLicense: initValue.some((item) => !!item.licenseNumber),
                licenses: initValue.map((item) => ({
                    ...item,
                    expireDate: item.expireDate ? dayjs(item.expireDate) : undefined,
                })),
            });
        }
    }, [initValue]);

    const onSubmit = async () => {
        const data = await form.validateFields();
        const licenseArrary =
            data.licenses?.map((item) => ({
                ...item,
                expireDate: item.expireDate ? item.expireDate.format('YYYY-MM-DD') : undefined,
                type: ELicenseType.DEA,
                licenseType: '',
            })) || [];
        const result = await providerLicenseBatchByType(licenseArrary, ELicenseType.DEA, scheduleToken);
        if (result.error) {
            message.error(result.error);
        } else {
            message.success('DEA license has been updated');
            await refetch?.();
        }
    };
    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => form.validateFields(),
    }));

    const handleRadioChange = (e: any) => {
        if (!e.target.value) {
            setShowRemoveWarning(true);
        }
    };

    const renderEditing = () => {
        return (
            <div className={s.wrap}>
                <Form form={form} initialValues={{ remember: true }} autoComplete="off" layout="vertical">
                    <Form.Item
                        label="Prescribe controlled substances"
                        name="needLicense"
                        rules={[{ required: true, message: 'This field is required' }]}
                    >
                        <Radio.Group onChange={handleRadioChange}>
                            <Space direction="vertical">
                                <Radio value>Yes, I will prescribe controlled substances</Radio>
                                <Radio value={false}>No, I do not prescribe controlled substances</Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    {needLicenseValue && (
                        <Form.List name="licenses" initialValue={[{}]}>
                            {(fields, { add, remove }) => {
                                const { length } = fields;

                                return (
                                    <div id="deaLicense">
                                        {fields.map((field, inx) => {
                                            return (
                                                <Form.Item key={field.key} noStyle>
                                                    <div className={s.modalWrap}>
                                                        <h3>{`DEA License ${inx + 1}`}</h3>
                                                        <Divider style={{ margin: '0 0 12px' }} />
                                                        <Form.Item
                                                            name={[field.name, 'id']}
                                                            style={{ position: 'absolute' }}
                                                        >
                                                            <Input type="hidden" />
                                                        </Form.Item>
                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="License state"
                                                            name={[field.name, 'state']}
                                                            validateFirst
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: 'License state is required',
                                                                },
                                                            ]}
                                                        >
                                                            <StateSelect />
                                                        </Form.Item>

                                                        <div className="flex gap-4">
                                                            <Form.Item
                                                                style={{ width: '320px' }}
                                                                label="DEA license number"
                                                                name={[field.name, 'licenseNumber']}
                                                                validateFirst
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: 'DEA license number is required',
                                                                    },
                                                                    FormValidationHelper.validateLicenseNumber(
                                                                        'License number must contain only letters or numbers',
                                                                    ),
                                                                ]}
                                                            >
                                                                <Input
                                                                    maxLength={25}
                                                                    placeholder="What is the DEA license number?  "
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                style={{ width: '320px' }}
                                                                label="Expiration date"
                                                                name={[field.name, 'expireDate']}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: 'Expiration date is required',
                                                                    },
                                                                ]}
                                                            >
                                                                <DatePicker
                                                                    disabledDate={(date) => {
                                                                        const now = new Date().getTime();
                                                                        if (!date) {
                                                                            return false;
                                                                        }
                                                                        return date?.valueOf() < now;
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                />
                                                            </Form.Item>
                                                        </div>
                                                        <Form.Item
                                                            style={{ width: '300px' }}
                                                            label="License copy"
                                                            name={[field.name, 'url']}
                                                            rules={[
                                                                { required: true, message: 'License copy is required' },
                                                            ]}
                                                        >
                                                            <FileUploadPrivate uploadType="LICENSE" />
                                                            {/* <FileUpload /> */}
                                                        </Form.Item>
                                                        {field.name !== 0 && (
                                                            <div className={s.removeWrap}>
                                                                <Button onClick={() => remove(inx)} danger>
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {field.name === length - 1 && (
                                                        <div
                                                            onClick={() => {
                                                                add();
                                                                const wrap = document.getElementById('deaLicense');
                                                                setTimeout(() => {
                                                                    wrap?.parentElement?.parentElement?.scroll({
                                                                        top: 1000000,
                                                                        behavior: 'smooth',
                                                                    });
                                                                }, 200);
                                                            }}
                                                            className={s.add}
                                                        >
                                                            + Add another DEA license
                                                        </div>
                                                    )}
                                                </Form.Item>
                                            );
                                        })}
                                    </div>
                                );
                            }}
                        </Form.List>
                    )}
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        const licensesArray: ILicenseData[] =
            initValue?.filter((item: ILicenseData) => !!(item && item.licenseNumber)) || [];
        if (!licensesArray?.length) {
            return <div>Not provided</div>;
        }
        return (
            <>
                {licensesArray?.map((item: ILicenseData) => {
                    return (
                        <div className={s.subModule} key={item.licenseNumber}>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '300px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>License state</div>
                                    <div className={s.value}>{item.state}</div>
                                </div>
                            </div>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '300px',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>DEA license number</div>
                                    <div className={s.value}>{item.licenseNumber}</div>
                                </div>
                                <div className={s.displayItem}>
                                    <div className={s.label}>Expiration date</div>
                                    <div className={s.value}>
                                        {item.expireDate ? dayjs(item.expireDate).format('MM-DD-YYYY') : ''}
                                    </div>
                                </div>
                            </div>
                            <div className={s.row}>
                                <div
                                    style={{
                                        width: '80%',
                                    }}
                                    className={s.displayItem}
                                >
                                    <div className={s.label}>Uploaded license</div>
                                    <div className={s.value}>
                                        <LicenseImg src={item.fullUrl || item.url} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <div>
            {isEditing ? renderEditing() : renderReview()}
            <Modal
                width={780}
                closable={false}
                open={showRemoveWarning}
                footer={
                    <div className={s.modalFooter}>
                        <Button
                            onClick={() => {
                                form.setFieldValue('needLicense', true);
                                setShowRemoveWarning(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                setShowRemoveWarning(false);
                            }}
                        >
                            Confirm
                        </Button>
                    </div>
                }
            >
                <div className={s.waringModalWrap}>
                    <div className={s.tipIcon} />
                    <div className={s.content}>
                        <div className={s.title}>Please confirm:</div>
                        <div className={s.descBox}>
                            <div className={s.desc}>
                                If you select this option, all of your DEA licenses will be deleted.
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
});

export default DeaLicense;
